import {
	CvTemplate,
	CvSectionBlock,
	CvColorScheme,
	CustomColors,
	CvLayout,
	SectionKey,
	HeaderSectionConfig,
	ContactSectionConfig,
	SummarySectionConfig,
	ExperienceSectionConfig,
	SkillsSectionConfig,
	CustomSectionConfig,
	isLegacySections,
	getColorScheme,
} from "@/types/cvTemplate";
import { Candidate } from "@/types/candidate";
import { CandidateExperience, CandidateSkill } from "@/hooks/candidate/useCandidateExperiences";
import { generateCvPdf } from "@/utils/pdf/cvPdf";
import { supabase } from "@/integrations/supabase/client";

// ── Shared helpers ──

function formatDate(dateStr: string | null): string {
	if (!dateStr) return "Present";
	try {
		const date = new Date(dateStr);
		return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
	} catch {
		return dateStr;
	}
}

function esc(value: string | undefined | null): string {
	if (!value) return "";
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

// ── Section renderers ──

function renderHeaderSection(
	config: HeaderSectionConfig,
	candidate: Candidate,
	colors: CvColorScheme,
	organizationName?: string
): string {
	const nameStyles: Record<string, string> = {
		bold: "font-weight:700; font-size:28px;",
		large: "font-weight:600; font-size:36px;",
		uppercase: "font-weight:700; font-size:28px; text-transform:uppercase; letter-spacing:2px;",
	};
	const style = nameStyles[config.nameStyle] || nameStyles.bold;

	let html = `<div class="cv-section cv-section--header" style="margin-bottom:24px; border-bottom:2px solid ${colors.primary}; padding-bottom:16px;">`;

	if (config.showLogo && config.logoUrl)
		html += `<img src="${esc(config.logoUrl)}" alt="${esc(organizationName)}" style="max-height:48px; margin-bottom:12px;" />`;

	if (config.anonymous)
		html += `<div style="${style} color:${colors.text};">Candidate Profile</div>`;
	else
		html += `<div style="${style} color:${colors.text};">${esc(candidate.name)}</div>`;

	if (candidate.function)
		html += `<div style="font-size:16px; color:${colors.secondary}; margin-top:4px;">${esc(candidate.function)}</div>`;

	html += `</div>`;
	return html;
}

function renderContactSection(
	config: ContactSectionConfig,
	candidate: Candidate,
	colors: CvColorScheme
): string {
	const items: string[] = [];

	if (config.showEmail && candidate.email)
		items.push(candidate.email);
	if (config.showPhone && candidate.phone)
		items.push(candidate.phone);
	if (config.showLocation && candidate.location)
		items.push(candidate.location);
	if (config.showLinkedin && candidate.linkedin)
		items.push(candidate.linkedin);

	if (items.length === 0) return "";

	return `<div class="cv-section cv-section--contact" style="margin-bottom:20px; padding:12px 16px; background:${colors.background}; border-radius:6px; font-size:13px; color:${colors.secondary};">
		${items.map(item => esc(item)).join(" &nbsp;•&nbsp; ")}
	</div>`;
}

function renderSummarySection(
	config: SummarySectionConfig,
	candidate: Candidate,
	colors: CvColorScheme,
	heading = "Professional Summary"
): string {
	const text = config.source === "custom" && config.customText
		? config.customText
		: candidate.ai_synthesis || "";

	if (!text) return "";

	return `<div class="cv-section cv-section--summary" style="margin-bottom:20px;">
		<h2 style="font-size:16px; font-weight:700; color:${colors.primary}; margin-bottom:8px; text-transform:uppercase; letter-spacing:1px;">${esc(heading)}</h2>
		<p style="font-size:14px; color:${colors.text}; line-height:1.7;">${esc(text)}</p>
	</div>`;
}

function renderExperienceSection(
	config: ExperienceSectionConfig,
	experiences: CandidateExperience[],
	colors: CvColorScheme,
	aiDescriptions?: Map<string, string>,
	heading = "Work Experience"
): string {
	if (experiences.length === 0) return "";

	const items = experiences.map(exp => {
		let html = `<div style="margin-bottom:14px;">`;
		html += `<div style="font-size:15px; font-weight:600; color:${colors.text};">${esc(exp.role)}</div>`;

		let subline = esc(exp.company);
		if (config.showIndustry && exp.industry)
			subline += ` — ${esc(exp.industry)}`;
		if (config.showDateRange)
			subline += ` &nbsp;|&nbsp; ${formatDate(exp.start_date)} – ${formatDate(exp.end_date)}`;

		html += `<div style="font-size:13px; color:${colors.secondary};">${subline}</div>`;

		const desc = aiDescriptions?.get(exp.id);
		if (desc)
			html += `<p style="font-size:13px; color:${colors.text}; line-height:1.6; margin-top:4px;">${esc(desc)}</p>`;

		html += `</div>`;
		return html;
	}).join("");

	return `<div class="cv-section cv-section--experience" style="margin-bottom:20px;">
		<h2 style="font-size:16px; font-weight:700; color:${colors.primary}; margin-bottom:8px; text-transform:uppercase; letter-spacing:1px;">${esc(heading)}</h2>
		${items}
	</div>`;
}

function renderSkillsSection(
	config: SkillsSectionConfig,
	skills: CandidateSkill[],
	candidate: Candidate,
	colors: CvColorScheme,
	heading = "Skills"
): string {
	const skillNames = skills.length > 0
		? skills.map(s => s.name)
		: (candidate.skills || []);

	if (skillNames.length === 0) return "";

	const inner = config.layout === "badges"
		? `<div style="display:flex; flex-wrap:wrap; gap:6px;">
			${skillNames.map(s => `<span style="display:inline-block; padding:4px 10px; background:${colors.background}; color:${colors.primary}; border:1px solid ${colors.primary}30; border-radius:4px; font-size:12px; font-weight:500;">${esc(s)}</span>`).join("")}
		</div>`
		: `<ul style="margin:0; padding-left:20px; columns:2; column-gap:24px;">
			${skillNames.map(s => `<li style="font-size:13px; color:${colors.text}; margin-bottom:4px;">${esc(s)}</li>`).join("")}
		</ul>`;

	return `<div class="cv-section cv-section--skills" style="margin-bottom:20px;">
		<h2 style="font-size:16px; font-weight:700; color:${colors.primary}; margin-bottom:8px; text-transform:uppercase; letter-spacing:1px;">${esc(heading)}</h2>
		${inner}
	</div>`;
}

function renderEducationSection(): string {
	// Stub — education data not yet in data model
	return "";
}

function renderCustomSection(config: CustomSectionConfig): string {
	if (!config.content) return "";

	return `<div class="cv-section cv-section--custom" style="margin-bottom:20px;">
		${config.title ? `<h2 style="font-size:16px; font-weight:700; color:#1e293b; margin-bottom:8px; text-transform:uppercase; letter-spacing:1px;">${esc(config.title)}</h2>` : ""}
		<div style="font-size:14px; color:#334155; line-height:1.6;">${config.content}</div>
	</div>`;
}

// ── Main renderer for structured sections ──

// Section heading translations
const SECTION_HEADINGS: Record<string, Record<string, string>> = {
	en: { summary: "Professional Summary", experience: "Work Experience", skills: "Skills" },
	fr: { summary: "Résumé Professionnel", experience: "Expérience Professionnelle", skills: "Compétences" },
	nl: { summary: "Professioneel Profiel", experience: "Werkervaring", skills: "Vaardigheden" },
	de: { summary: "Berufliches Profil", experience: "Berufserfahrung", skills: "Fähigkeiten" },
	es: { summary: "Resumen Profesional", experience: "Experiencia Laboral", skills: "Habilidades" },
};

function getSectionHeading(section: string, language?: string): string {
	const lang = language && SECTION_HEADINGS[language] ? language : "en";
	return SECTION_HEADINGS[lang][section] || SECTION_HEADINGS.en[section] || section;
}

interface RenderOptions {
	sections: CvSectionBlock[];
	candidate: Candidate;
	experiences: CandidateExperience[];
	skills: CandidateSkill[];
	organizationName?: string;
	colorScheme?: string;
	customColors?: CustomColors;
	layout?: CvLayout;
	language?: string;
	polishedSummary?: string;
	polishedExperiences?: Map<string, string>;
}

const SIDEBAR_SECTIONS: SectionKey[] = ["contact", "skills"];

export function generateHtmlFromSections({
	sections,
	candidate,
	experiences,
	skills,
	organizationName,
	colorScheme,
	customColors,
	layout = "standard",
	language,
	polishedSummary,
	polishedExperiences,
}: RenderOptions): string {
	const colors = getColorScheme(colorScheme || "classic", customColors);
	const sorted = [...sections]
		.filter(b => b.enabled)
		.sort((a, b) => a.order - b.order);

	const isCompact = layout === "compact";
	const sizeMultiplier = isCompact ? 0.85 : 1;

	// If AI provided a polished summary, use a modified candidate object
	const effectiveCandidate = polishedSummary
		? { ...candidate, ai_synthesis: polishedSummary }
		: candidate;

	const headings = {
		summary: getSectionHeading("summary", language),
		experience: getSectionHeading("experience", language),
		skills: getSectionHeading("skills", language),
	};

	const renderers: Record<SectionKey, (config: unknown) => string> = {
		header: (config) => renderHeaderSection(config as HeaderSectionConfig, candidate, colors, organizationName),
		contact: (config) => renderContactSection(config as ContactSectionConfig, candidate, colors),
		summary: (config) => renderSummarySection(config as SummarySectionConfig, effectiveCandidate, colors, headings.summary),
		experience: (config) => renderExperienceSection(config as ExperienceSectionConfig, experiences, colors, polishedExperiences, headings.experience),
		skills: (config) => renderSkillsSection(config as SkillsSectionConfig, skills, candidate, colors, headings.skills),
		education: () => renderEducationSection(),
		custom: (config) => renderCustomSection(config as CustomSectionConfig),
	};

	if (layout === "sidebar") {
		const sidebarBlocks = sorted.filter(b => SIDEBAR_SECTIONS.includes(b.key));
		const mainBlocks = sorted.filter(b => !SIDEBAR_SECTIONS.includes(b.key));

		const sidebarHtml = sidebarBlocks.map(b => renderers[b.key]?.(b.config) || "").join("");
		const mainHtml = mainBlocks.map(b => renderers[b.key]?.(b.config) || "").join("");

		return `<div style="display:flex; gap:24px;">
			<div style="width:35%; flex-shrink:0; padding:16px; background:${colors.background}; border-radius:8px;">${sidebarHtml}</div>
			<div style="flex:1; min-width:0;">${mainHtml}</div>
		</div>`;
	}

	const html = sorted.map(b => renderers[b.key]?.(b.config) || "").join("");

	if (isCompact)
		return `<div style="font-size:${Math.round(14 * sizeMultiplier)}px; line-height:1.4;">${html}</div>`;

	return html;
}

// ── Legacy renderer (variable replacement) ──

function replaceSimpleVariables(
	html: string,
	candidate: Candidate,
	skills: CandidateSkill[],
	organizationName?: string
): string {
	const skillsStr = skills.map(s => s.name).join(", ") ||
		(candidate.skills || []).join(", ");

	const replacements: Record<string, string> = {
		"candidate.name": candidate.name || "",
		"candidate.email": candidate.email || "",
		"candidate.phone": candidate.phone || "",
		"candidate.function": candidate.function || "",
		"candidate.location": candidate.location || "",
		"candidate.linkedin": candidate.linkedin || "",
		"candidate.skills": skillsStr,
		"candidate.summary": candidate.ai_synthesis || "",
		"organization.name": organizationName || "",
		"system.current_date": new Date().toLocaleDateString("en-US", {
			month: "long",
			day: "numeric",
			year: "numeric",
		}),
	};

	let result = html;
	for (const [key, value] of Object.entries(replacements)) {
		const regex = new RegExp(`\\{\\{\\s*${key.replace(".", "\\.")}\\s*\\}\\}`, "g");
		result = result.replace(regex, value);
	}

	return result;
}

function processExperienceLoop(
	html: string,
	experiences: CandidateExperience[]
): string {
	const eachRegex = /\{\{#each\s+experiences\}\}([\s\S]*?)\{\{\/each\}\}/g;

	return html.replace(eachRegex, (_match, template) => {
		if (experiences.length === 0)
			return "";

		return experiences.map(exp => {
			let block = template;
			const expReplacements: Record<string, string> = {
				"experience.role": exp.role || "",
				"experience.company": exp.company || "",
				"experience.industry": exp.industry || "",
				"experience.start_date": formatDate(exp.start_date),
				"experience.end_date": formatDate(exp.end_date),
			};

			for (const [key, value] of Object.entries(expReplacements)) {
				const regex = new RegExp(`\\{\\{\\s*${key.replace(".", "\\.")}\\s*\\}\\}`, "g");
				block = block.replace(regex, value);
			}

			return block;
		}).join("");
	});
}

// ── Public API ──

export interface PolishedCvContent {
	summary?: string;
	experiences: Map<string, string>;
}

interface GenerateCvHtmlOptions {
	template: CvTemplate;
	candidate: Candidate;
	experiences: CandidateExperience[];
	skills: CandidateSkill[];
	organizationName?: string;
	sections?: CvSectionBlock[] | string[];
	language?: string;
	polished?: PolishedCvContent;
}

export function generateCvHtml({
	template,
	candidate,
	experiences,
	skills,
	organizationName,
	sections,
	language,
	polished,
}: GenerateCvHtmlOptions): string {
	const effectiveSections = sections || template.sections;

	// Structured sections → block renderer
	if (!isLegacySections(effectiveSections)) {
		const html = generateHtmlFromSections({
			sections: effectiveSections as CvSectionBlock[],
			candidate,
			experiences,
			skills,
			organizationName,
			colorScheme: template.color_scheme,
			customColors: template.custom_colors,
			layout: template.layout,
			language,
			polishedSummary: polished?.summary,
			polishedExperiences: polished?.experiences,
		});

		const styles = template.styles || "";
		return `<div class="cv-generated"><style>${styles}</style>${html}</div>`;
	}

	// Legacy path → variable replacement
	let html = "";

	if (template.header_html)
		html += template.header_html;

	let content = template.content;
	content = replaceSimpleVariables(content, candidate, skills, organizationName);
	content = processExperienceLoop(content, experiences);
	html += content;

	if (template.footer_html)
		html += template.footer_html;

	const styles = template.styles || "";
	return `<div class="cv-generated" data-sections="${(effectiveSections as string[]).join(",")}"><style>${styles}</style>${html}</div>`;
}

export async function polishCvWithAi(
	candidate: Candidate,
	experiences: CandidateExperience[],
	skills: CandidateSkill[],
	language?: string
): Promise<PolishedCvContent> {
	const { data, error } = await supabase.functions.invoke("polish-cv-content", {
		body: {
			summary: candidate.ai_synthesis || "",
			candidate_function: candidate.function || "",
			experiences: experiences.map(exp => ({
				id: exp.id,
				role: exp.role,
				company: exp.company,
				industry: exp.industry || "",
				start_date: exp.start_date || "",
				end_date: exp.end_date || "",
			})),
			skills: skills.map(s => s.name),
			language: language || "en",
		},
	});

	if (error)
		throw new Error(`AI polish failed: ${error.message}`);

	const expMap = new Map<string, string>();
	if (data?.experiences && Array.isArray(data.experiences))
		for (const exp of data.experiences)
			if (exp.id && exp.description)
				expMap.set(exp.id, exp.description);

	return {
		summary: data?.summary || undefined,
		experiences: expMap,
	};
}

export async function generateAndSaveCv({
	template,
	candidate,
	experiences,
	skills,
	organizationName,
	sections,
	language,
	polishWithAi,
	onProgress,
}: GenerateCvHtmlOptions & {
	polishWithAi?: boolean;
	onProgress?: (step: string) => void;
}): Promise<{ success: boolean; documentId?: string }> {
	// Step 0: AI polish (optional)
	let polished: PolishedCvContent | undefined;
	if (polishWithAi) {
		onProgress?.("polishingWithAi");
		try {
			polished = await polishCvWithAi(candidate, experiences, skills, language);
		} catch {
			// AI polish failed — continue without it
		}
	}

	// Step 1: Generate HTML
	onProgress?.("replacingVariables");
	const htmlContent = generateCvHtml({
		template,
		candidate,
		experiences,
		skills,
		organizationName,
		sections,
		language,
		polished,
	});

	// Step 2: Generate PDF
	onProgress?.("generatingPdf");
	const filename = `CV_${candidate.name.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`;
	const pdfBlob = await generateCvPdf({
		htmlContent,
		filename,
		styles: template.styles || undefined,
	});

	// Step 3: Upload PDF directly to storage (avoids edge function payload limit)
	onProgress?.("savingDocument");
	const sanitizedName = filename.replace(/[^0-9a-zA-Z!\-_.*'()]/g, '');
	const storagePath = `${candidate.id}/cv/${Date.now()}_${sanitizedName}`;

	const { data: uploadData, error: uploadError } = await supabase.storage
		.from("cvs")
		.upload(storagePath, pdfBlob, {
			contentType: "application/pdf",
			upsert: true,
		});

	if (uploadError)
		throw new Error(`Failed to upload PDF: ${uploadError.message}`);

	// Step 4: Create document record
	const { data: { user } } = await supabase.auth.getUser();
	const { data: userOrg } = await supabase
		.from("user_organization")
		.select("organization_uuid")
		.eq("user_uuid", user!.id)
		.single();

	const { data: docData, error: docError } = await supabase
		.from("documents")
		.insert({
			name: filename,
			display_name: `CV - ${template.name}`,
			organization_uuid: userOrg!.organization_uuid,
			storage_id: uploadData.id,
			entity_id: candidate.id,
			category: "cv",
			created_by: user!.id,
			exclude_from_ai: true,
			comments: `Generated from template: ${template.name}`,
		})
		.select()
		.single();

	if (docError)
		throw new Error(`Failed to create document record: ${docError.message}`);

	return {
		success: true,
		documentId: docData?.id,
	};
}
